import React from 'react'
import { func, array, bool, string, object } from 'prop-types'
import cx from 'classnames'
import { injectIntl } from 'react-intl'
// TODO: Don't even try to use new 'react-dropzone', it will not work in widget
import Dropzone from 'react-dropzone'
import axios from 'axios'
import { getErrorMessage, logError } from 'src/utils/helpers'
import { postRequest } from 'src/utils/request-client'
import notification from 'src/components/Notification'
import intlMessagesApp from 'src/modules/App/translations'

// Prepare for canceling upload post request
let CancelUploadToken
let cancelUploadSource

class UploadDropZone extends React.Component {
  state = {
    isDragActive: false,
    isUploadInProgress: false,
    uploadProgress: 0,
  }

  acceptedFileTypes = () => {
    if (this.props.supportedUploadTypes) {
      return this.props.supportedUploadTypes
    }

    // If not supported upload file types are not defined, accept all of these as default
    return [
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/gif',
      'image/bmp',
      'application/msword',
      'application/pdf',
      'application/x-gzip',
      'application/zip',
      'application/x-rar-compressed',
      'application/x-zip-compressed',
      'multipart/x-zip',
    ]
  }

  /**
   * Upload selected files one by one
   *
   * @param {object} file File to upload
   * @param {FormData} formData Data for post request (where file will be added)
   * @returns {Promise<object>}
   */
  uploadFile = async (file, formData) => {
    const { onDropHandlerUrl, onDropSuccess, onDropError, intl } = this.props

    this.setState({
      isUploadInProgress: true,
    })

    // Update token for each request
    CancelUploadToken = axios.CancelToken
    cancelUploadSource = CancelUploadToken.source()

    formData.append('file', file)

    return await postRequest(
      {
        base: onDropHandlerUrl,
        mock: false,
      },
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: e => {
          const progress = Math.round((e.loaded * 100) / (e.total || 1))
          this.setState({
            uploadProgress: progress,
          })
        },
        cancelToken: cancelUploadSource.token,
      },
    )
      .then(response => {
        this.setState({
          isUploadInProgress: false,
        })

        if (onDropSuccess && typeof onDropSuccess === 'function') {
          onDropSuccess(response)
        }

        return response
      })
      .catch(e => {
        this.setState({
          isUploadInProgress: false,
        })

        if (onDropError && typeof onDropError === 'function') {
          onDropError(e)
        }

        // Get error from response or take default one
        const errorMessage = getErrorMessage(
          e,
          intl.formatMessage(intlMessagesApp.appErrorDescription),
        )

        notification.error({
          message: intl.formatMessage(intlMessagesApp.appErrorTitle),
          description: errorMessage,
          duration: 10,
        })

        logError('Upload to "' + onDropHandlerUrl + '" failed', { errorMessage, rawError: e })

        return e
      })
  }

  /**
   * Upload selected files one by one
   *
   * @param {array} files Selected files
   * @returns {bool|void}
   */
  defaultOnDrop = async files => {
    const { onDropHandlerUrl, onDropData, onAllFilesAdded } = this.props

    if (!files.length) {
      return false
    }

    if (!onDropHandlerUrl) {
      logError('Upload destination not provided', null, 1)
      return false
    }

    // Prepare data for multipart/form-data
    const formData = new FormData()

    // Attach additional data
    const data = onDropData && typeof onDropData === 'object' ? onDropData : {}
    const additionalData = {
      allowedFileTypes: this.acceptedFileTypes(),
      ...data,
    }
    formData.append('additionalData', JSON.stringify(additionalData))

    // Upload all selected files one by one
    for (const key in files) {
      if (files.hasOwnProperty(key)) {
        await this.uploadFile(files[key], formData)
      }
    }

    if (onAllFilesAdded && typeof onAllFilesAdded === 'function') {
      onAllFilesAdded()
    }
  }

  onDragLeave = () => {
    this.setState({
      isDragActive: false,
    })
  }

  onDragOver = () => {
    this.setState({
      isDragActive: true,
    })
  }

  handleOnDrop = files => {
    const { onDrop } = this.props

    this.setState({
      isDragActive: false,
    })

    if (onDrop && typeof onDrop === 'function') {
      onDrop(files)
    } else {
      this.defaultOnDrop(files)
    }
  }

  render() {
    const {
      renderContent,
      renderDragActiveContent,
      multiple,
      disableSelectionOnClick,
      fullSpread,
      classNameHolder,
      classNameDropZone,
      classNameInner,
      dropZoneRef,
      children,
    } = this.props

    const { isDragActive, isUploadInProgress, uploadProgress } = this.state

    const classNamesHolder = cx({
      [classNameHolder]: !!classNameHolder,
    })

    const classNamesDropZone = cx({
      [classNameDropZone]: !!classNameDropZone,
    })

    const classNamesInner = cx({
      [classNameInner]: !!classNameInner,
    })

    const onClick = e => {
      if (disableSelectionOnClick) {
        e.stopPropagation()
      }
    }

    // const cancelUpload = () => {
    //   // Cancel currently active upload
    //   cancelUploadSource.cancel()
    // }

    const accept = this.acceptedFileTypes()

    const dropZoneStyle = fullSpread
      ? {
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          top: 0,
          height: '100%',
        }
      : {}

    return (
      <div className={classNamesHolder}>
        <Dropzone
          ref={dropZoneRef}
          disableClick={!!disableSelectionOnClick}
          style={dropZoneStyle}
          className={classNamesDropZone}
          accept={accept.join(', ')}
          multiple={multiple}
          onDrop={this.handleOnDrop}
          onDragLeave={this.onDragLeave}
          onDragOver={this.onDragOver}
        >
          <div className={classNamesInner} onClick={onClick}>
            {isDragActive &&
            renderDragActiveContent &&
            typeof renderDragActiveContent === 'function'
              ? renderDragActiveContent()
              : ''}

            {/* Render content passes as param (so we can return back some values) */}
            {renderContent && typeof renderContent === 'function'
              ? renderContent({
                  isDragActive,
                  isUploadInProgress,
                  uploadProgress,
                })
              : ''}

            {/* Render content passes as children */}
            {children}
          </div>
        </Dropzone>
      </div>
    )
  }
}

UploadDropZone.defaultProps = {
  multiple: true,
  disableSelectionOnClick: false,
  fullSpread: false,
}

UploadDropZone.propTypes = {
  supportedUploadTypes: array,
  onDrop: func,
  onDropHandlerUrl: string,
  onDropData: object,
  onDropSuccess: func,
  onAllFilesAdded: func,
  onDropError: func,
  renderDragActiveContent: func,
  multiple: bool,
  disableSelectionOnClick: bool,
  fullSpread: bool,
  classNameHolder: string,
  classNameDropZone: string,
  classNameInner: string,
  intl: object,
}

export default injectIntl(UploadDropZone)
